<template>
  <div class="location-edit" v-if="localLocation">
    <v-form ref="form" @submit.prevent="save">
      <headline bg>
        {{ $t("location.additional-information") }}
      </headline>

      <v-row>
        <!-- Colonne gauche : Formulaire -->
        <v-col>
          <v-row>
            <v-col>
              <v-text-field
                  :label="$t('name')"
                  v-model="localLocation.name"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <tree-select-organization
                  :label="$t('organization.organization')"
                  v-model="localLocation.organizationId"
              />
            </v-col>
          </v-row>



         <v-card class="my-6">
           <v-card-text>
             <v-row>
               <v-col md="2">
                 <v-text-field
                     :label="$t('number')"
                     v-model="localLocation.address.number"
                     hide-details
                 />
               </v-col>
               <v-col md="10">
                 <v-text-field
                     :label="$t('street')"
                     v-model="localLocation.address.street"
                     hide-details
                 />
               </v-col>
             </v-row>

             <v-row>
               <v-col>
                 <v-text-field
                     :label="$t('additional-information')"
                     v-model="localLocation.address.additionalInformation"
                     hide-details
                 />
               </v-col>
             </v-row>

             <v-row>
               <v-col>
                 <v-text-field
                     :label="$t('zipcode')"
                     v-model="localLocation.address.zipcode"
                     hide-details
                 />
               </v-col>
               <v-col>
                 <v-text-field
                     :label="$t('city')"
                     v-model="localLocation.address.city"
                     hide-details
                 />
               </v-col>
               <v-col>
                 <v-text-field
                     :label="$t('state')"
                     v-model="localLocation.address.state"
                     hide-details
                 />
               </v-col>
               <v-col>
                 <available-select
                     type="country"
                     v-model="localLocation.address.country"
                 />
               </v-col>
             </v-row>
           </v-card-text>
         </v-card>

          <v-row>
            <v-col>
              <v-switch
                  :label="$t('publish')"
                  v-model="localLocation.publish"
                  hide-details
              />
            </v-col>
            <v-col>
              <v-switch
                  :label="$t('private')"
                  v-model="localLocation.private"
                  hide-details
              />
            </v-col>
            <v-col>
              <v-switch
                  :label="$t('En maintenance')"
                  v-model="localLocation.inMaintenance"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-select
                  v-model="localLocation.type"
                  :label="$t('location-type')"
                  :items="types"
                  item-text="label"
                  item-value="value"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-select
                  v-model="localLocation.ownerType"
                  :label="$t('location.owner-types')"
                  :items="ownerTypes"
                  item-text="label"
                  item-value="value"
                  hide-details
              />
            </v-col>
            <v-col>
              <v-select
                  v-model="localLocation.accessibility"
                  :label="$t('accessibility')"
                  :items="accessibilities"
                  item-text="label"
                  item-value="value"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <available-select
                  type="timezone"
                  :label="$t('location.timezone')"
                  v-model="localLocation.timezone"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <localized-string
                  :label="$t('location.additional-information')"
                  v-model="localLocation.direction"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-select
                  v-model="localLocation.facilities"
                  :label="$t('facilities')"
                  :items="facilities"
                  item-text="label"
                  item-value="value"
                  hide-details
                  chips
                  deletable-chips
                  small-chips
                  multiple
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Colonne droite : Carte et coordonnées -->
        <v-col>
          <v-row>
            <v-col>
              <div class="map-block" v-if="localLocation && localLocation.coordinates">
                <Map
                    ref="map-container"
                    :zoom="16"
                    @move="centerMarker"
                    @zoomlevelschange="centerMarker"
                    @drag="centerMarker"
                    :on-map-ready="onMapReady"
                    :show-center-control="!editingPosition"
                >
                  <l-marker
                      v-if="marker"
                      :icon="selectedIcon"
                      :lat-lng="marker"
                  />
                </Map>
              </div>
              <v-row class="mt-1">
                <v-col>
                  <v-btn
                      @click="editingPosition = !editingPosition"
                      x-small
                      :color="editingPosition ? 'success' : 'default'"
                  >
                    <v-icon left>{{ editingPosition ? 'mdi-check' : 'mdi-pencil' }}</v-icon>
                    {{ editingPosition ? $t('location.finish-editing-position') : $t('location.edit-position') }}
                  </v-btn>
                </v-col>
                <v-col class="text-right">
                  <v-btn @click="getPositionFromAddress" x-small>
                    <v-icon left>mdi-crosshairs</v-icon>
                    {{ $t('location.set-location-from-address') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field
                  :label="$t('latitude')"
                  type="number"
                  v-model.number="localLocation.coordinates.latitude"
                  hide-details
              />
            </v-col>
            <v-col>
              <v-text-field
                  :label="$t('longitude')"
                  type="number"
                  v-model.number="localLocation.coordinates.longitude"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-switch
                  :label="$t('twenty-four-seven')"
                  v-model="localLocation.twentyFourSeven"
                  hide-details
              />
            </v-col>
            <v-col>
              <v-switch
                  :label="$t('location.charging-when-closed')"
                  v-model="localLocation.chargingWhenClosed"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-row v-if="!localLocation.twentyFourSeven">
            <v-col>
              <div class="label">
                {{ $t('location.openingTimes') }}
              </div>
              <opening-times v-model="localLocation.openingTimes"/>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex justify-center">
          <v-btn class="mr-5" @click="cancel">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="primary" type="submit">
            {{ $t('save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <v-dialog max-width="600" v-model="showAddressSuggestionModal">
      <v-card>
        <v-card-title>
          {{ $t('location.select-location') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-text-field
                v-model="searchSuggest"
                :loading="loadingSuggest"
                :label="$t('location.find-existing-location')"
                :placeholder="$t('start-typing-to-search')"
                prepend-icon="mdi-map-search-outline"
                clearable
              ></v-text-field>
              <v-data-table
                  :headers="[
                      {text: $t('address'), value: 'address'},
                      {text: '', value: 'options'},
                  ]"
                  :items="addresses"
                  hide-default-footer
              >
                <template #item.address="{item}">
                  {{ item.address }}<template v-if="item.location">, {{ item.location }}</template>
                </template>
                <template #item.options="{item}">
                  <v-btn small color="primary" @click="setLocationFromPlace(item)">
                    {{ $t('select') }}
                  </v-btn>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col class="text-right">
              <v-btn @click="showAddressSuggestionModal=false" small>
                {{ $t('close') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LocationRepository from "@repository/LocationRepository";
import RulesMixin from "@mixins/RulesMixin";
import LocalizedString from "@blocks/LocalizedString.vue";
import AvailableSelect from "@blocks/Select/AvailableSelect.vue";
import OpeningTimes from "@blocks/OpeningTimes.vue";
import Map from "@blocks/Map.vue";
import {Icon, LatLng} from "leaflet";
import {LMarker} from "vue2-leaflet";
import iconSelected from "@images/marker-selected.svg";
import iconShadow from "@images/marker-shadow.png";
import { v4 as uuidv4 } from 'uuid';
import TreeSelectOrganization from "@blocks/Select/TreeSelectOrganization.vue";

export default {
  components: {
    TreeSelectOrganization,
    LocalizedString,
    OpeningTimes,
    Map,
    LMarker,
    AvailableSelect,
  },
  data() {
    return {
      localLocation: {
        "company": null,
        "customId": null,
        "email": null,
        "externalId": null,
        "name": null,
        "organizationId": null,
      },
      selectedIcon: new Icon({
        iconUrl: iconSelected,
        iconSize: [50, 72],
        iconAnchor: [25, 72],
        shadowUrl: iconShadow,
        shadowSize: [41, 41],
        shadowAnchor: [13, 41]
      }),
      marker: null,
      mapReady: false,
      editingPosition: false,
      showAddressSuggestionModal: false,
      loadingSuggest: false,
      searchSuggest: null,
      sessionId: null,
      addresses: [],
    }
  },

  mixins: [RulesMixin],

  watch: {
    location(val) {
      this.localLocation = this.cloneObj(val)
    },

    localLocation: {
      handler(val) {
        this.setUnsavedChanges()
      },
      deep: true
    },

    searchSuggest() {
      this.loadSearchSuggest()
    },
  },

  mounted() {
    if (!this.currentUser.permissions.includes('CUSTOMER_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }

    this.sessionId = uuidv4()
    this.localLocation = this.cloneObj(this.location)
    this.localLocation.organizationId = this.localLocation.organization?.id

    this.$nextTick(() => {
      this.cleanUnsavedChanges()
    })

  },

  computed: {

    location() {
      return this.$parent.location
    },

    types() {
      return [
        "ON_STREET",
        "PARKING_GARAGE",
        "UNDERGROUND_GARAGE",
        "PARKING_LOT",
        "OTHER",
        "UNKNOWN",
      ].map(value => ({label: this.$t(`enums.location-type.${value}`), value }))
    },

    ownerTypes() {
      return [
        "HOME",
        "STORE",
        "OFFICE",
        "EV_STATION",
        "PUBLIC_SITE",
        "PARKING",
        "OTHER",
        "UNKNOWN",
      ].map(value => ({label: this.$t(`enums.location-owner-type.${value}`), value }))
    },

    accessibilities() {
      return [
        "FREE_ACCESS",
        "RESTRICTED_ACCESS",
        "PAID_ACCESS",
        "TEST_STATION",
      ].map(value => ({label: this.$t(`enums.location-accessibility.${value}`), value }))
    },

    facilities() {
      return [
        "HOTEL",
        "RESTAURANT",
        "CAFE",
        "MALL",
        "SUPERMARKET",
        "SPORT",
        "RECREATION_AREA",
        "NATURE",
        "MUSEUM",
        "BUS_STOP",
        "TAXI_STAND",
        "TRAIN_STATION",
        "AIRPORT",
        "CARPOOL_PARKING",
        "FUEL_STATION",
        "WIFI",
      ].map(value => ({label: this.$t(`enums.location-facility.${value}`), value }))
    },
  },

  methods: {

    centerMarker() {
      if (this.mapReady && this.editingPosition) {
        let map = this.$refs['map-container'].$refs.map.mapObject;
        let center = map.getCenter()
        this.marker = center
        this.localLocation.coordinates.latitude = center.lat
        this.localLocation.coordinates.longitude = center.lng
      }
    },

    onMapReady(map) {
      let center = new LatLng(this.localLocation.coordinates.latitude, this.localLocation.coordinates.longitude)
      map.panTo(center)
      this.marker = center
      this.$nextTick(() => {
        this.mapReady = true
      })
    },

    getPositionFromAddress() {
      let tmp = []
      if(this.localLocation.address.number) {
        tmp.push(this.localLocation.address.number)
      }
      if(this.localLocation.address.street) {
        tmp.push(this.localLocation.address.street)
      }
      if(this.localLocation.address.additionalInformation) {
        tmp.push(this.localLocation.address.additionalInformation)
      }
      if(this.localLocation.address.city) {
        tmp.push(this.localLocation.address.city)
      }
      if(this.localLocation.address.zipcode) {
        tmp.push(this.localLocation.address.zipcode)
      }
      if(this.localLocation.address.country) {
        tmp.push(this.localLocation.address.country)
      }
      let query = tmp.join(' ')

      this.searchSuggest = query
      this.showAddressSuggestionModal = true
    },

    loadSearchSuggest() {
      if (this.searchSuggest) {
        this.loadingSuggest = true;
        LocationRepository.searchAddress(this.searchSuggest, this.sessionId).then(response => {
          this.addresses = response
          this.loadingSuggest = false;
        }).catch(err => {
          this.notifyError(err)
          this.loadingSuggest = false;
        })
      }
    },

    setLocationFromPlace(val) {
      if(val) {
        this.showLoading(true)
        LocationRepository.getPlace(val.placeId, this.sessionId).then(result => {
          this.localLocation.address.number = result.detail.number
          this.localLocation.address.street = result.detail.street
          this.localLocation.address.additionalInformation = result.detail.additionalInformation
          this.localLocation.address.city = result.detail.city
          this.localLocation.address.zipcode = result.detail.zipcode
          this.localLocation.address.state = result.detail.state
          this.localLocation.address.country = result.detail.country
          this.setMapPosition(new LatLng(result.coordinates.lat, result.coordinates.lng))

          this.sessionId = uuidv4();

          this.showAddressSuggestionModal = false
          this.showLoading(false)

        }).catch(e => {
          this.notifyError(e)
          this.showLoading(false)
        })
      }
    },

    setMapPosition(position) {
      if(position?.lat) {
        this.$refs['map-container']?.$refs.map.mapObject.panTo(position)
        this.marker = [position.lat, position.lng]
        if(!this.localLocation.coordinates) {
          this.localLocation.coordinates = {}
        }
        this.localLocation.coordinates.latitude = position.lat
        this.localLocation.coordinates.longitude = position.lng
      }
    },

    save() {
      if (this.$refs.form.validate()) {
        this.showLoading(true)
        if (!this.localLocation.enabled) {
          this.localLocation.enabled = false
        }
        LocationRepository.update(this.$route.params.id, this.localLocation).then(response => {
          this.$dialog.notify.success(this.$t('updated-successfully'))
          this.$parent.loadLocation().then(() => {
            this.showLoading(false)
            this.cleanUnsavedChanges()
            this.$router.replace({name: 'location.detail'})
          })
        }).catch(err => {
          this.notifyError(err)
          this.showLoading(false)
        })
      }
    },

    cancel() {
      this.$router.replace({name: 'location.detail'})
    },

  },
}
</script>

<style lang="scss">
.location-edit {
  .map-block {
    border: 3px solid #E53C22;
    border-radius: 0 10px 10px 0;
    overflow: hidden;
    height: 400px;
  }

  .label {
    color: rgba(0, 0, 0, 0.38);
    font-size: 12px;
  }
}
</style>
